import { z } from 'zod';

import { BankTransactionDirection, BankTransactionPaymentMethod } from '../generated/enums.js';

/**
 * Input for extracting structured hints from a single bank transaction description string.
 *
 * This is intended for 1:1 usage (per transaction) to avoid prompt context blow-ups
 * when processing large statements.
 */
export const DescriptionRawExtractionInputSchema = z.object({
  descriptionRaw: z.string().min(1),
});
export type DescriptionRawExtractionInput = z.infer<typeof DescriptionRawExtractionInputSchema>;

/**
 * Output of DESCRIPTION_RAW_EXTRACTION.
 *
 * Notes:
 * - counterpartyDocument can be either a CPF/CNPJ (digits only, 11/14) OR a masked banking identifier
 *   extracted from tokens like "***592858**" (digits only).
 * - counterpartyName MUST be a clean, name-like segment and MUST NOT equal descriptionRaw.
 */
export const DescriptionRawExtractionOutputSchema = z.object({
  counterpartyName: z.string().nullish(),
  counterpartyDocument: z.string().nullish(),
  paymentMethod: z.nativeEnum(BankTransactionPaymentMethod).nullish(),
  referenceCode: z.string().nullish(),
  inferredDirection: z
    .union([z.nativeEnum(BankTransactionDirection), z.literal('UNKNOWN')])
    .nullish(),
  inferredDirectionConfidence: z.number().min(0).max(100).nullish(),
  aiConfidence: z
    .object({
      counterpartyName: z.number().min(0).max(100),
      counterpartyDocument: z.number().min(0).max(100).nullish(),
      paymentMethod: z.number().min(0).max(100).nullish(),
      referenceCode: z.number().min(0).max(100).nullish(),
    })
    .nullish(),
  warnings: z.array(z.string()).nullish(),
});

export type DescriptionRawExtractionOutput = z.infer<typeof DescriptionRawExtractionOutputSchema>;
